import { MediaType } from '@qse/api'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { FileExplorer } from '@/components'
// import { FileInput } from '@/components/FileInput'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

interface SearchValues {
  search: string
  fileType?: string
  isDir?: boolean
  createdFrom?: string
  createdTo?: string
}

const SearchResults = () => {
  const query = useQuery()
  const searchQuery = query.get('query') || ''
  const fileType = query.get('fileType') || ''
  const isDir = query.get('isDir') || ''
  const createdFrom = query.get('createdFrom') || ''
  const createdTo = query.get('createdTo') || ''

  const searchValues = useMemo<SearchValues>(
    () => ({
      search: searchQuery,
      fileType: fileType || undefined,
      isDir: isDir === 'true' ? true : undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    }),
    [searchQuery, fileType, isDir, createdFrom, createdTo],
  )

  // Search parameters for FileExplorer
  // Search results page always searches globally using location: 'HOME'
  const searchParams = useMemo(
    () => ({
      limit: 50,
      search: searchValues.search || undefined,
      isDir: searchValues.isDir === true ? true : undefined,
      mediaType:
        searchValues.isDir === true ? undefined : (searchValues.fileType as MediaType) || undefined,
      createdFrom: searchValues.createdFrom || undefined,
      createdTo: searchValues.createdTo || undefined,
      location: 'HOME' as const, // Always search globally in search results page
    }),
    [searchValues],
  )

  const emptyStateConfig = {
    icon: (
      <svg
        className="w-12 h-12 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
        />
      </svg>
    ),
    title: searchValues.search
      ? `No files found for "${searchValues.search}"`
      : 'No search results',
    description: searchValues.search
      ? `We couldn't find any files matching "${searchValues.search}"`
      : 'Try searching for files, folders, or specific file types.',
  }

  // Generate title based on active filters
  const getTitle = () => {
    const parts = []

    if (searchValues.search) {
      parts.push(`Search: "${searchValues.search}"`)
    }

    if (searchValues.fileType) {
      const fileTypeLabels: Record<string, string> = {
        FOLDER: 'Folder',
        DOCUMENT: 'Document',
        IMAGE: 'Image',
        VIDEO: 'Video',
        AUDIO: 'Audio',
        SPREADSHEET: 'Spreadsheet',
        PRESENTATION: 'Presentation',
        PDF: 'PDF',
        ARCHIVE: 'Archive',
        OTHER: 'Other Files',
      }

      parts.push(`Type: ${fileTypeLabels[searchValues.fileType] || searchValues.fileType}`)
    }

    if (searchValues.createdFrom || searchValues.createdTo) {
      parts.push('Date Filtered')
    }

    return parts.length > 0 ? parts.join(' | ') : 'Search Results'
  }

  return (
    <div className="flex flex-col gap-4 text-gray-900 dark:text-white min-h-screen">
      {/* Header */}
      <div className="px-4 md:px-[30px] lg:block hidden">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate max-w-[600px]">
          {getTitle()}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Search results across all your files and folders
        </p>
      </div>

      {/* Example: FileInput with correct queryKey for cache invalidation after upload */}
      {/**
      <FileInput invalidateQueryKeys={[queryKeys.files.list(searchParams)]} />
      */}

      <div className="flex-1 pb-32">
        <FileExplorer emptyStateConfig={emptyStateConfig} searchParams={searchParams} title="" />
      </div>
    </div>
  )
}

export default SearchResults
